import { LOGIN_PAGE_URL } from 'constants/utils';

import { Auth0ContextInterface, useAuth0, User } from '@auth0/auth0-react';
import {
  clearFilesSorts,
  deleteClientsListSize,
  deleteMobileInfoBannerClosed,
  getRefreshTokenFromLSAuth0,
  getUserActiveCookie,
  removeClientListScrollPosition,
  setAutoLogoutRedirectUrl,
  setUserActiveCookie
} from 'modules/networkTools/localStorageTokens';
import { logout } from 'modules/networkTools/networkTools';
import { useEffect } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useLocation, useSearchParams } from 'react-router-dom';
import { clearInterval, setInterval } from 'worker-timers';
import { useAppDispatch } from 'store/hooks';
import { deleteSearchQueryDraft } from 'store/myTodos/myTodosActions';

// this hook is used to logout the user when the browser is closed, it sets a cookie with expiry of 5 minutes then checks for that cookie after the app is reopened
export const useBeforeExitLogout = ({ disabled }: { disabled: boolean }) => {
  const { logout: auth0logout }: Auth0ContextInterface<User> = useAuth0();

  const { pathname } = useLocation();
  const logUserOutOnBrowserClose = () => {
    if (disabled) {
      setUserActiveCookie(300);
      return;
    }
    auth0logout({
      logoutParams: {
        returnTo: window.location.origin + LOGIN_PAGE_URL
      }
    });
    logout();
  };

  const isOnExcludedPaths =
    pathname === '/' ||
    pathname.includes('auth0') ||
    pathname.includes('permissions') ||
    pathname.includes('invite') ||
    pathname.includes('auth0Error');

  const checkIfCookieExist = () => {
    if (!isOnExcludedPaths) {
      const cookie = getUserActiveCookie();

      if (!cookie) {
        logUserOutOnBrowserClose();
      }
    }
  };

  useEffect(() => {
    checkIfCookieExist();
    const interval = setInterval(() => {
      checkIfCookieExist();
      if (isOnExcludedPaths) {
        setUserActiveCookie(300);
      } else {
        setUserActiveCookie(60);
      }
    }, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [pathname]);
};

export const useAutoLogout = <T extends () => void>(idleTime: number, disabled: boolean) => {
  const dispatch = useAppDispatch();
  const { logout: auth0logout }: Auth0ContextInterface<User> = useAuth0();
  const [searchParams] = useSearchParams();

  const context = searchParams.get('context');
  const handleLogout = async () => {
    const lsRefreshToken = getRefreshTokenFromLSAuth0('auth0spajs');

    if (context === 'popup') {
      window.close();
      return;
    }
    localStorage.removeItem('lastPage');
    removeClientListScrollPosition();
    deleteClientsListSize();
    deleteMobileInfoBannerClosed();
    clearFilesSorts();
    if (lsRefreshToken) {
      await dispatch(deleteSearchQueryDraft());
      setAutoLogoutRedirectUrl(window.location.pathname);
      auth0logout({
        logoutParams: {
          returnTo: window.location.origin + LOGIN_PAGE_URL
        }
      });
      logout();
    }
  };

  useIdleTimer({
    timeout: idleTime * 60_000,
    onIdle: handleLogout,
    debounce: 0,
    crossTab: true,
    disabled,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange',
      'focus'
    ]
  });
};
